import React, { useEffect, useRef, useState } from "react";
import api, { API, getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Mic, Square, Loader2, ShieldAlert, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { logger } from "@/lib/logger";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

// Self-contained voice recorder using MediaRecorder. Browser-native; no
// external lib required. Returns audio Blob via onComplete.
function useRecorder() {
  const [state, setState] = useState("idle"); // idle | recording | stopping
  const [seconds, setSeconds] = useState(0);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.start();
      recRef.current = rec;
      setSeconds(0);
      setState("recording");
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (e) {
      logger.error("Mic access denied", e);
      toast.error("Could not access microphone — please allow permission");
    }
  };

  const stop = () =>
    new Promise((resolve) => {
      const rec = recRef.current;
      if (!rec) return resolve(null);
      setState("stopping");
      clearInterval(timerRef.current);
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        rec.stream.getTracks().forEach((t) => t.stop());
        recRef.current = null;
        setState("idle");
        resolve(blob);
      };
      rec.stop();
    });

  return { state, seconds, start, stop };
}

export default function Apology() {
  const { user, setUser } = useAuth();
  const [content, setContent] = useState("");
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const rec = useRecorder();

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/apologies/me");
      setHistory(data);
    } catch (e) {
      logger.error("load apologies", e);
    }
  };

  useEffect(() => { loadHistory(); }, []);

  const transcribe = async () => {
    if (rec.state !== "recording") return;
    const blob = await rec.stop();
    if (!blob) return;
    setTranscribing(true);
    try {
      const form = new FormData();
      form.append("file", blob, "apology.webm");
      const token = getToken();
      const resp = await fetch(`${API}/apologies/transcribe`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || "Transcription failed");
      }
      const { text } = await resp.json();
      setContent((c) => (c ? `${c}\n${text}` : text));
      toast.success("Voice transcribed");
    } catch (e) {
      toast.error(e.message || "Could not transcribe");
    } finally {
      setTranscribing(false);
    }
  };

  const submit = async () => {
    if (content.trim().length < 20) {
      toast.error("Please write at least 20 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post("/apologies", { content: content.trim() });
      toast.success("Apology submitted for review");
      setContent("");
      await loadHistory();
      // Refresh user state to show pending status
      const { data: me } = await api.get("/auth/me");
      setUser(me);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit");
    } finally {
      setBusy(false);
    }
  };

  const isDeactivated = user && user !== false && !user.active;
  const hasPending = history.some((h) => h.status === "pending");

  return (
    <div className="min-h-screen bg-zinc-50">
      <div className="max-w-2xl mx-auto px-6 py-10 space-y-6">
        <div>
          <div className="inline-flex items-center gap-2 text-red-700 mb-2">
            <ShieldAlert className="w-5 h-5" />
            <span className="text-[11px] uppercase tracking-widest font-bold">Account Deactivated</span>
          </div>
          <h1 className="font-display text-4xl font-black tracking-tighter">Submit an apology letter</h1>
          <p className="text-sm text-zinc-600 mt-2">
            Your account has been deactivated after 3 hard warnings. Write a sincere apology below — or record it as a voice note and we&apos;ll transcribe it for you. A manager will review it shortly.
          </p>
        </div>

        {hasPending ? (
          <div className="border-l-4 border-amber-500 bg-amber-50 rounded-md p-4" data-testid="apology-pending">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-amber-900">Your apology is under review</div>
                <div className="text-sm text-amber-800 mt-1">
                  Sit tight — a manager will get back to you. If they ask for more details, you&apos;ll be able to resubmit here.
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white border border-zinc-200 rounded-md p-5 space-y-3" data-testid="apology-form">
            <div>
              <label className="label-overline">Your apology</label>
              <Textarea
                rows={8}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Write your apology here, or use the microphone to dictate it…"
                data-testid="apology-text"
                disabled={!isDeactivated && !content}
              />
              <div className="text-[11px] text-zinc-500 mt-1">{content.length} / minimum 20 characters</div>
            </div>

            <div className="flex items-center justify-between border-t border-zinc-200 pt-3">
              <div className="flex items-center gap-3">
                {(() => {
                  if (rec.state === "recording") {
                    return (
                      <Button
                        onClick={transcribe}
                        variant="outline"
                        className="border-red-300 text-red-700"
                        data-testid="apology-stop-record"
                      >
                        <Square className="w-4 h-4 mr-2 fill-red-700" />
                        Stop &amp; transcribe ({rec.seconds}s)
                      </Button>
                    );
                  }
                  if (transcribing) {
                    return (
                      <Button disabled variant="outline" data-testid="apology-transcribing">
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Transcribing…
                      </Button>
                    );
                  }
                  return (
                    <Button onClick={rec.start} variant="outline" data-testid="apology-record">
                      <Mic className="w-4 h-4 mr-2" /> Record voice
                    </Button>
                  );
                })()}
              </div>
              <Button
                onClick={submit}
                disabled={busy || content.trim().length < 20}
                className="bg-zinc-950 hover:bg-zinc-800"
                data-testid="apology-submit"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Submit apology
              </Button>
            </div>
          </div>
        )}

        {history.length > 0 && (
          <div className="bg-white border border-zinc-200 rounded-md p-5" data-testid="apology-history">
            <h3 className="font-display text-lg font-bold tracking-tight mb-3">History</h3>
            <div className="space-y-3">
              {history.map((a) => (
                <ApologyHistoryRow key={a.id} a={a} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ApologyHistoryRow({ a }) {
  const palette = {
    pending: "border-amber-400 bg-amber-50 text-amber-900",
    approved: "border-emerald-500 bg-emerald-50 text-emerald-900",
    more_info: "border-orange-500 bg-orange-50 text-orange-900",
  };
  const Icon = a.status === "approved" ? CheckCircle2 : AlertCircle;
  return (
    <div className={`border-l-4 rounded-md p-3 ${palette[a.status] || "border-zinc-300 bg-zinc-50"}`} data-testid={`apology-row-${a.id}`}>
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold">
          <Icon className="w-3.5 h-3.5" /> {a.status.replace("_", " ")}
        </span>
        <span className="text-[11px] text-zinc-500">{fmtDate(a.created_at)}</span>
      </div>
      <div className="mt-1 text-sm whitespace-pre-wrap">{a.content}</div>
      {a.decision_note && (
        <div className="mt-2 text-[12px] italic text-zinc-700 border-t border-zinc-200 pt-2">
          Reviewer note: {a.decision_note}
        </div>
      )}
    </div>
  );
}
